import { Typography } from '../typography'
import { ArrowRightIcon } from 'primitives/icons/arrows'

import { Link } from './link'
import { ImportantNotice } from '../demo/important-notice'
import { CodeExample } from '../demo/code-example'
import { DemoTabs } from '../demo/demo-tabs'

export function LinkDemo() {
  return (
    <div className="space-y-2">
      <Typography variant="cf-heading-02" className="text-cf-grey-900">
        Link
      </Typography>

      <DemoTabs
        tabs={{
          preview: (
            <>
              <ImportantNotice type="warning" label="💡 How to keep the locale in the URL">
                This component does not handle locale routing. To keep the locale in the URL, please
                use the <code>@repo/web-ui/components/link</code> component instead. The web-ui Link
                component automatically handles locale prefixes and provides proper routing support
                for internationalized urls.
                <CodeExample
                  code={`
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/design-system-cf/components/link'
import { Link as WebLink } from '@repo/web-ui/components/link'

<Link asChild variant="primary-green">
  <WebLink href={AppRoutes.SUBSCRIPTION}>
    <span>Your Link Text</span>
  </WebLink>
</Link>`}
                />
              </ImportantNotice>

              <div className="space-y-6">
                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Variants
                  </Typography>
                  <div className="space-y-4">
                    <div className="flex flex-row gap-8">
                      <Link href="#">Primary Link</Link>
                      <Link href="#" variant="subtle">
                        Subtle Link
                      </Link>
                    </div>
                    <CodeExample
                      code={`import { Link } from '@repo/design-system-cf/components/link'

// Standard variants
<Link href="#">Primary Link</Link>
<Link href="#" variant="subtle">Subtle Link</Link>`}
                    />
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6 bg-cf-purple-900">
                  <Typography variant="cf-heading-03" className="text-cf-grey-50 mb-4">
                    Inverse Variants
                  </Typography>
                  <div className="space-y-4">
                    <div className="flex flex-row gap-8 rounded-lg">
                      <Link href="#" variant="primary-inverse">
                        Primary Inverse Link
                      </Link>
                      <Link href="#" variant="subtle-inverse">
                        Subtle Inverse Link
                      </Link>
                    </div>
                    <CodeExample
                      lightLink
                      code={`import { Link } from '@repo/design-system-cf/components/link'

// Inverse variants
<Link href="#" variant="primary-inverse">Primary Inverse Link</Link>
<Link href="#" variant="subtle-inverse">Subtle Inverse Link</Link>`}
                    />
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Sizes
                  </Typography>
                  <div className="space-y-4">
                    <div className="flex flex-row gap-8">
                      <Link href="#" size="regular">
                        Regular
                      </Link>
                      <Link href="#" size="small">
                        Small
                      </Link>
                    </div>
                    <CodeExample
                      code={`import { Link } from '@repo/design-system-cf/components/link'

// Sizes
<Link href="#" size="regular">Regular</Link>
<Link href="#" size="small">Small</Link>`}
                    />
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Features
                  </Typography>
                  <div className="space-y-4">
                    <div className="flex flex-row gap-8">
                      <Link href="#" inline>
                        Inline variant
                      </Link>
                      <Link href="#" icon={<ArrowRightIcon />}>
                        Link with Icon
                      </Link>
                    </div>
                    <CodeExample
                      code={`import { Link } from '@repo/design-system-cf/components/link'
import { ArrowRightIcon } from 'primitives/icons/arrows'

// Features
<Link href="#" inline>Inline variant</Link>
<Link href="#" icon={<ArrowRightIcon />}>Link with Icon</Link>`}
                    />
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Unofficial variants
                  </Typography>
                  <Typography variant="body-01" className="mb-4">
                    These variants are already in use within the app. They have been included as
                    custom options here to simplify the eventual migration to the official design
                    system variants.
                  </Typography>
                  <div className="flex flex-wrap gap-4 mt-8">
                    <Link href="#" variant="primary-green">
                      Primary Green Link
                    </Link>
                  </div>
                  <CodeExample
                    code={`import { Link } from '@repo/design-system-cf/components/link'

// Unofficial variants
<Link href="#" variant="primary-green">Primary Green Link</Link>`}
                  />
                </div>
              </div>
            </>
          ),
          design: (
            <iframe
              width="100%"
              height="100%"
              src="https://embed.figma.com/design/c8oXlEODLmj3csp3ux5DKu/Design-System---Components?node-id=673-3363&m=dev&embed-host=share"
              allowFullScreen
            />
          ),
          api: {
            properties: [
              {
                name: 'variant',
                type: "'primary' | 'primary-green' | 'subtle' | 'primary-inverse' | 'subtle-inverse'",
                defaultValue: "'primary'",
                description: 'The visual style of the link',
              },
              {
                name: 'size',
                type: "'regular' | 'small'",
                defaultValue: "'regular'",
                description: 'The size of the link text',
              },
              {
                name: 'className',
                type: 'string',
                defaultValue: 'undefined',
                description: 'Additional CSS classes to apply. Overrides default variant styles.',
              },
              {
                name: 'href',
                type: 'string',
                defaultValue: 'undefined',
                description:
                  'The URL the link points to. Required for standard `<a>` tag behavior.',
              },
              {
                name: 'asChild',
                type: 'boolean',
                defaultValue: 'false',
                description:
                  'Whether to merge props onto the child instead of creating a new `<a>` element. Useful for integrating with routing libraries or custom components.',
              },
              {
                name: 'underline',
                type: 'boolean',
                defaultValue: 'false',
                description: 'Whether the link should always be underlined.',
              },
              {
                name: 'icon',
                type: 'React.ReactNode',
                defaultValue: 'undefined',
                description: 'Optional icon to display with the link.',
              },
            ],
          },
          changelog: [
            {
              date: '2025-05-13',
              changes: [
                'Added support for primary-green and suble variants',
                'Added small size option',
                'Added icon prop support',
                'Added underline prop for always underlined links',
              ],
            },
          ],
        }}
      />
    </div>
  )
}
